/**
 * 고용 상세 관리 모달 (V45.2 → V45.4)
 *
 * 직원 1명의 고용 관련 모든 작업을 한 곳에서 처리:
 * - 퇴사 예약/변경/취소
 * - 계약 연장 예약/취소 (V45.4: 예약 방식으로 변경)
 * - 고용형태 변경 예약/변경/취소 (V45.4: endDate 필수 추가)
 *
 * V45.4 변경사항:
 * - 계약만료 확정 탭 제거
 * - 계약 연장을 예약 방식으로 변경 (scheduleContractExtension)
 * - 고용형태 변경 시 endDate 필수 (파견/인턴/계약직만)
 * - 액션 완료 시 성공 팝업 + 모달 닫기 + 목록 리프레시
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  AlertCircle,
  UserMinus,
  RefreshCw,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Lock,
  PauseCircle,
} from 'lucide-react';
// V66 Phase 2: 휴직 섹션
import { LeaveOfAbsenceSection } from './LeaveOfAbsenceSection';
import { useModalOverlayClose } from '../../../../shared/hooks/useModalOverlayClose';
import {
  EmploymentManagementItem,
  EmploymentTypeCode,
  TerminationReasonCode,
  TERMINATION_REASON_NAMES,
  useScheduleTermination,
  useChangeTerminationDate,
  useCancelTermination,
  useScheduleContractExtension,
  useCancelContractExtension,
  useScheduleEmploymentTypeChange,
  useCancelEmploymentTypeChange,
} from '../../../../shared/hooks/useEmploymentManagement';

interface EmploymentDetailManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: EmploymentManagementItem | null;
  onSuccess?: () => void;
  forceTab?: TabType; // V253: 외부에서 강제 지정 시 탭 바 숨기고 해당 액션만 렌더
  onBack?: () => void; // V255: 액션 피커로 돌아가기
  readOnly?: boolean; // V61: role_exec 등 조회 전용 모드 (액션 버튼 비활성화)
}

// V45.4: contractExpiry 탭 제거
// V66 Phase 2: 'leaveOfAbsence' 탭 추가 (HR 휴직 등록/복직/취소)
export type TabType = 'termination' | 'extension' | 'typeChange' | 'leaveOfAbsence';

const EMPLOYMENT_TYPE_OPTIONS: { code: EmploymentTypeCode; name: string }[] = [
  { code: 'DISPATCH', name: '파견직' },
  { code: 'INTERN', name: '인턴' },
  { code: 'CONTRACT', name: '계약직' },
  { code: 'REGULAR', name: '정규직' },
];

export function EmploymentDetailManagementModal({
  isOpen,
  onClose,
  item,
  onSuccess,
  forceTab,
  onBack,
  readOnly = false,
}: EmploymentDetailManagementModalProps) {
  const overlayProps = useModalOverlayClose(onClose);
  const [activeTab, setActiveTab] = useState<TabType>(forceTab || 'termination');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // 퇴사 예약 상태
  const [terminationDate, setTerminationDate] = useState('');
  const [terminationReasonCode, setTerminationReasonCode] = useState<TerminationReasonCode>('VOLUNTARY');
  const [terminationNote, setTerminationNote] = useState('');
  const [allowPastTerminationDate, setAllowPastTerminationDate] = useState(false); // V46: 과거 퇴사일 입력 허용 옵션

  // V254: 예약 실행일 상태 (3개 액션 공통 패턴)
  // V60: 예약 실행일은 적용일과 자동 동기화 — 사용자 수정 불가
  const [terminationPlannedRunDate, setTerminationPlannedRunDate] = useState('');
  const [extensionPlannedRunDate, setExtensionPlannedRunDate] = useState('');
  const [typeChangePlannedRunDate, setTypeChangePlannedRunDate] = useState('');

  // V45.4: 계약 연장 예약 상태
  const [extensionEffectiveDate, setExtensionEffectiveDate] = useState('');
  const [extensionNewEndDate, setExtensionNewEndDate] = useState('');
  const [extensionReason, setExtensionReason] = useState('');

  // V45.4: 고용형태 변경 상태 (endDate 추가)
  const [newEmploymentType, setNewEmploymentType] = useState<EmploymentTypeCode>('REGULAR');
  const [typeChangeEffectiveDate, setTypeChangeEffectiveDate] = useState('');
  const [typeChangeEndDate, setTypeChangeEndDate] = useState(''); // V45.4: endDate 추가
  const [typeChangeReason, setTypeChangeReason] = useState('');
  // V64: 정규직 전환 시 전환가산
  const [conversionBonusYears, setConversionBonusYears] = useState<number>(0);
  const [conversionBonusYearsError, setConversionBonusYearsError] = useState('');

  // Hooks
  const scheduleTermination = useScheduleTermination();
  const changeTerminationDate = useChangeTerminationDate();
  const cancelTermination = useCancelTermination();
  // V45.4: 계약 연장 예약 훅으로 변경
  const scheduleExtension = useScheduleContractExtension();
  const cancelExtension = useCancelContractExtension();
  const scheduleTypeChange = useScheduleEmploymentTypeChange();
  const cancelTypeChange = useCancelEmploymentTypeChange();

  // 모달 열릴 때 초기화
  useEffect(() => {
    if (isOpen && item) {
      // V253: forceTab이 있으면 해당 탭으로 강제 설정
      if (forceTab) {
        setActiveTab(forceTab);
      }

      // V76: 퇴사 예약 초기화 (endDate = leaveDate 정책)
      // 정책: 퇴사일 = 계약만료일 (동일 날짜, +1일 폐지)
      if (item.terminationPlanned && item.terminationPlannedEndDate) {
        // 예약이 있으면: DB의 값 그대로 (= 퇴사일)
        let terminationDateValue = item.terminationPlannedEndDate;
        if (item.endDate && terminationDateValue < item.endDate) {
          terminationDateValue = item.endDate;
        }
        setTerminationDate(terminationDateValue);
        setTerminationReasonCode(item.terminationReasonCode || 'VOLUNTARY');
        setTerminationNote(item.terminationReasonNote || '');
      } else {
        // V76: 예약이 없으면 endDate 그대로 (퇴사일 = 계약만료일)
        const terminationDateValue = item.endDate || getDefaultDate(30);
        setTerminationDate(terminationDateValue);
        setTerminationReasonCode('VOLUNTARY');
        setTerminationNote('');
      }
      // V46: 과거 퇴사일 입력 허용 옵션 초기화
      setAllowPastTerminationDate(false);
      // V254+V60: 퇴사 실행일 = 퇴사일과 동일 (자동 동기화)
      setTerminationPlannedRunDate(item.terminationPlannedEndDate || terminationDate || '');

      // V45.7: 계약 연장 예약 초기화
      // 정책: 적용일은 항상 endDate + 1일 이상이어야 함
      if (item.contractExtensionPlanned && item.contractExtensionPlannedEffectiveDate) {
        // 예약이 있으면: 저장된 값 사용, 단 endDate 이하라면 endDate + 1일로 보정
        let effectiveDate = item.contractExtensionPlannedEffectiveDate;
        if (item.endDate && effectiveDate <= item.endDate) {
          effectiveDate = getNextDay(item.endDate);
        }
        setExtensionEffectiveDate(effectiveDate);
        setExtensionNewEndDate(item.contractExtensionPlannedNewEndDate || getDefaultExtensionDate(item.endDate));
      } else {
        // V45.7: endDate가 있으면 endDate + 1일, 없으면 오늘
        const defaultEffectiveDate = item.endDate ? getNextDay(item.endDate) : getToday();
        setExtensionEffectiveDate(defaultEffectiveDate);
        setExtensionNewEndDate(getDefaultExtensionDate(item.endDate));
      }
      setExtensionReason('');
      // V254+V60: 연장 실행일 = 적용일과 동일 (자동 동기화)
      setExtensionPlannedRunDate(extensionEffectiveDate || '');

      // V45.7: 고용형태 변경 초기화
      // 정책: 적용일은 항상 endDate + 1일 이상이어야 함
      if (item.employmentTypeChangePlanned) {
        // 예약이 있으면: 저장된 값 사용, 단 endDate 이하라면 endDate + 1일로 보정
        setNewEmploymentType(item.employmentTypeChangeTargetCode || 'REGULAR');
        let effectiveDate = item.employmentTypeChangePlannedEffectiveDate || (item.endDate ? getNextDay(item.endDate) : getToday());
        if (item.endDate && effectiveDate <= item.endDate) {
          effectiveDate = getNextDay(item.endDate);
        }
        setTypeChangeEffectiveDate(effectiveDate);
        // 만료일: 저장된 값 or 적용일 + 1년 - 1일
        const defaultEndDate = subtractDaysLocal(addYearsLocal(effectiveDate, 1), 1);
        const finalEndDate = item.employmentTypeChangeTargetEndDate || defaultEndDate;
        setTypeChangeEndDate(finalEndDate);
      } else {
        // 예약이 없으면: endDate + 1일
        setNewEmploymentType(getNextEmploymentType(item.employmentTypeCode));
        const defaultEffectiveDate = item.endDate ? getNextDay(item.endDate) : getToday();
        setTypeChangeEffectiveDate(defaultEffectiveDate);
        const defaultEndDate = subtractDaysLocal(addYearsLocal(defaultEffectiveDate, 1), 1);
        setTypeChangeEndDate(defaultEndDate);
      }
      setTypeChangeReason('');
      // V254+V60: 고용형태변경 실행일 = 적용일과 동일 (자동 동기화)
      setTypeChangePlannedRunDate(typeChangeEffectiveDate || '');
      // V64: 전환가산 초기화
      setConversionBonusYears(0);
      setConversionBonusYearsError('');

      setError('');
      setSuccessMessage('');
    }
  }, [isOpen, item]);

  // ========================================
  // V45.7: String-Safe 날짜 헬퍼 (UTC 변환 없음)
  // ========================================

  const pad2 = (n: number) => String(n).padStart(2, '0');

  // Date 객체를 YYYY-MM-DD 문자열로 변환 (로컬 기준)
  const formatLocalYMD = (d: Date) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

  // YYYY-MM-DD 문자열을 Date 객체로 변환 (로컬 기준)
  const parseLocalYMD = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  // YYYY-MM-DD 문자열에 N일 추가
  const addDaysYMD = (dateStr: string, days: number) => {
    const dt = parseLocalYMD(dateStr);
    dt.setDate(dt.getDate() + days);
    return formatLocalYMD(dt);
  };

  // YYYY-MM-DD 문자열에 N년 추가
  const addYearsYMD = (dateStr: string, years: number) => {
    const dt = parseLocalYMD(dateStr);
    dt.setFullYear(dt.getFullYear() + years);
    return formatLocalYMD(dt);
  };

  // 오늘 날짜 (YYYY-MM-DD)
  const getTodayYMD = () => formatLocalYMD(new Date());

  // ========================================
  // 기존 함수들을 String-Safe 함수로 교체
  // ========================================

  // 기본 날짜 계산 (오늘 + days일)
  const getDefaultDate = (days: number) => addDaysYMD(getTodayYMD(), days);

  // 기본 연장일 (현재 종료일 + 1년)
  const getDefaultExtensionDate = (currentEndDate?: string) =>
    currentEndDate ? addYearsYMD(currentEndDate, 1) : addYearsYMD(getTodayYMD(), 1);

  // 다음 고용형태 추천
  const getNextEmploymentType = (current: EmploymentTypeCode): EmploymentTypeCode => {
    const order: EmploymentTypeCode[] = ['DISPATCH', 'INTERN', 'CONTRACT', 'REGULAR'];
    const currentIndex = order.indexOf(current);
    if (currentIndex < order.length - 1) {
      return order[currentIndex + 1];
    }
    return 'REGULAR';
  };

  // 오늘 날짜
  const getToday = () => getTodayYMD();

  // 특정 날짜의 다음 날 (YYYY-MM-DD 형식)
  const getNextDay = (dateStr: string) => addDaysYMD(dateStr, 1);

  // 날짜에 N년 추가 (YYYY-MM-DD 형식)
  const addYearsLocal = (dateStr: string, years: number) => addYearsYMD(dateStr, years);

  // 날짜에서 N일 차감 (YYYY-MM-DD 형식)
  const subtractDaysLocal = (dateStr: string, days: number) => addDaysYMD(dateStr, -days);

  // 특정 날짜의 이전 날 (YYYY-MM-DD 형식)
  const getPreviousDay = (dateStr: string) => addDaysYMD(dateStr, -1);

  // 잠금 여부 확인
  const isLocked = item?.isLocked || false;

  // V45.4: 성공 처리 공통 - 성공 알림 + 모달 닫기 + 목록 리프레시
  const handleSuccess = async (message: string) => {
    // 알림 표시
    alert(message);
    setError('');
    setSuccessMessage('');

    // V46 Fix(A): 목록 리프레시 완료 후 모달 닫기
    // selectedItem 동기화는 부모 컴포넌트의 useEffect가 자동 처리
    if (onSuccess) {
      await onSuccess();
    }

    // 모달 닫기
    onClose();
  };

  // ==================== 퇴사 예약 ====================
  const handleScheduleOrChangeTermination = async () => {
    if (!item) return;
    setError('');
    setSuccessMessage('');

    // 유효성 검증
    if (!terminationDate) {
      setError('퇴사일을 입력해주세요.');
      return;
    }
    // V46: 과거 퇴사일 최종 검증 (안전장치)
    const isPastDate = terminationDate < getToday();
    if (isPastDate && !allowPastTerminationDate) {
      setError('과거 퇴사일을 입력하려면 "과거 퇴사일 입력 허용" 옵션을 체크해야 합니다.');
      return;
    }
    // V45.6: 과거일 확인 팝업 (옵션 ON인 경우만 도달)
    if (isPastDate) {
      const confirmed = window.confirm(
        `입력한 퇴사일(${terminationDate})은 과거입니다.\n즉시 퇴사 처리됩니다. 계속하시겠습니까?`
      );
      if (!confirmed) return;
    }
    // V76: 퇴사일은 endDate까지 가능 (계약만료일 = 퇴사일)
    if (item.endDate && terminationDate > item.endDate) {
      setError(`퇴사일은 계약 종료일(${item.endDate})까지 가능합니다.`);
      return;
    }
    if (terminationNote.trim().length > 200) {
      setError('사유 코멘트는 최대 200자까지 입력 가능합니다.');
      return;
    }

    try {
      // V76: 퇴사일 그대로 전송 (계약만료일 = 퇴사일, -1일 변환 폐지)
      const leaveDate = terminationDate;

      // V60: 실행일은 항상 적용일과 동일 — 서버가 강제 동기화하므로 전달 생략
      const termRunDate = undefined;

      if (item.terminationPlanned) {
        // 변경
        await changeTerminationDate.mutate(item.historyId, leaveDate, termRunDate);
        handleSuccess(isPastDate ? `퇴사가 즉시 처리되었습니다. (퇴사일: ${terminationDate})` : `퇴사 예약이 변경되었습니다. (퇴사일: ${terminationDate})`);
      } else {
        // 신규 예약
        await scheduleTermination.mutate(
          item.historyId,
          leaveDate,
          terminationReasonCode,
          terminationNote.trim() || undefined,
          undefined, // reason (하위 호환)
          termRunDate // V254
        );
        handleSuccess(isPastDate ? `퇴사가 즉시 처리되었습니다. (퇴사일: ${terminationDate})` : `퇴사가 예약되었습니다. (퇴사일: ${terminationDate})`);
      }
    } catch (err: any) {
      setError(err.message || '처리 중 오류가 발생했습니다.');
    }
  };

  const handleCancelTermination = async () => {
    if (!item) return;
    if (!window.confirm(`${item.employeeName}님의 퇴사 예약을 취소하시겠습니까?`)) return;

    setError('');
    setSuccessMessage('');

    try {
      await cancelTermination.mutate(item.historyId);
      handleSuccess('퇴사 예약이 취소되었습니다.');
    } catch (err: any) {
      setError(err.message || '처리 중 오류가 발생했습니다.');
    }
  };

  // ==================== V45.4: 계약 연장 예약 ====================
  const handleScheduleOrExtendContract = async () => {
    if (!item) return;
    setError('');
    setSuccessMessage('');

    // 유효성 검증
    if (!extensionEffectiveDate) {
      setError('적용일을 입력해주세요.');
      return;
    }
    if (!extensionNewEndDate) {
      setError('새 계약 종료일을 입력해주세요.');
      return;
    }
    if (item.endDate && extensionNewEndDate <= item.endDate) {
      setError(`새 종료일은 현재 종료일(${item.endDate})보다 이후여야 합니다.`);
      return;
    }

    // V45.4: 과거일 확인 팝업
    const isPastDate = extensionEffectiveDate < getToday();
    if (isPastDate) {
      const confirmed = window.confirm(
        `입력한 적용일(${extensionEffectiveDate})은 과거입니다.\n즉시 계약 연장이 됩니다. 계속하시겠습니까?`
      );
      if (!confirmed) return;
    }

    try {
      // V60: 실행일은 항상 적용일과 동일 — 서버가 강제 동기화하므로 전달 생략
      const extRunDate = undefined;
      await scheduleExtension.mutate(
        item.historyId,
        extensionEffectiveDate,
        extensionNewEndDate,
        extensionReason.trim() || undefined,
        extRunDate // V254
      );
      handleSuccess(isPastDate ? '계약이 즉시 연장되었습니다.' : '계약 연장이 예약되었습니다.');
    } catch (err: any) {
      setError(err.message || '처리 중 오류가 발생했습니다.');
    }
  };

  const handleCancelExtension = async () => {
    if (!item) return;
    if (!window.confirm(`${item.employeeName}님의 계약 연장 예약을 취소하시겠습니까?`)) return;

    setError('');
    setSuccessMessage('');

    try {
      await cancelExtension.mutate(item.historyId);
      handleSuccess('계약 연장 예약이 취소되었습니다.');
    } catch (err: any) {
      setError(err.message || '처리 중 오류가 발생했습니다.');
    }
  };

  // V45.4: endDate 필수 여부 확인 (파견/인턴/계약직이면 endDate 필수)
  const isEndDateRequired = (typeCode: EmploymentTypeCode): boolean => {
    return typeCode === 'DISPATCH' || typeCode === 'INTERN' || typeCode === 'CONTRACT';
  };

  // ==================== 고용형태 변경 ====================
  const handleScheduleTypeChange = async () => {
    if (!item) return;
    setError('');
    setSuccessMessage('');

    // 유효성 검증
    if (!typeChangeEffectiveDate) {
      setError('적용일을 입력해주세요.');
      return;
    }
    if (newEmploymentType === item.employmentTypeCode) {
      setError('현재와 동일한 고용형태입니다.');
      return;
    }

    // V45.4: endDate 필수 검증 (파견/인턴/계약직)
    if (isEndDateRequired(newEmploymentType)) {
      if (!typeChangeEndDate) {
        setError(`${EMPLOYMENT_TYPE_OPTIONS.find(o => o.code === newEmploymentType)?.name || '해당 고용형태'}는 만료일 입력이 필수입니다.`);
        return;
      }
      if (typeChangeEndDate <= typeChangeEffectiveDate) {
        setError('만료일은 적용일보다 이후여야 합니다.');
        return;
      }
    }

    if (typeChangeReason.trim().length > 200) {
      setError('사유는 최대 200자까지 입력 가능합니다.');
      return;
    }

    // V64: 정규직 전환 시 전환가산 검증 (정수만 허용)
    if (newEmploymentType === 'REGULAR' && conversionBonusYearsError) {
      setError('전환가산은 정수만 입력 가능합니다.');
      return;
    }
    if (newEmploymentType === 'REGULAR' && conversionBonusYears < 0) {
      setError('전환가산은 음수일 수 없습니다.');
      return;
    }

    // V45.4: 과거일 확인 팝업
    const isPastDate = typeChangeEffectiveDate < getToday();
    if (isPastDate) {
      const confirmed = window.confirm(
        `입력한 적용일(${typeChangeEffectiveDate})은 과거입니다.\n즉시 고용형태 변경이 됩니다. 계속하시겠습니까?`
      );
      if (!confirmed) return;
    }

    try {
      // V64: 정규직 전환 시 conversionBonusYears 전달
      const bonusToSend = newEmploymentType === 'REGULAR' && conversionBonusYears > 0 ? conversionBonusYears : undefined;
      // V60: 실행일은 항상 적용일과 동일 — 서버가 강제 동기화하므로 전달 생략
      const tcRunDate = undefined;
      await scheduleTypeChange.mutate(
        item.historyId,
        newEmploymentType,
        typeChangeEffectiveDate,
        typeChangeReason.trim() || undefined,
        isEndDateRequired(newEmploymentType) ? typeChangeEndDate : undefined,
        bonusToSend, // V64: 전환가산
        tcRunDate // V254
      );
      handleSuccess(isPastDate ? '고용형태가 즉시 변경되었습니다.' : '고용형태 변경이 예약되었습니다.');
    } catch (err: any) {
      setError(err.message || '처리 중 오류가 발생했습니다.');
    }
  };

  const handleCancelTypeChange = async () => {
    if (!item) return;
    if (!window.confirm(`${item.employeeName}님의 고용형태 변경 예약을 취소하시겠습니까?`)) return;

    setError('');
    setSuccessMessage('');

    try {
      await cancelTypeChange.mutate(item.historyId);
      handleSuccess('고용형태 변경 예약이 취소되었습니다.');
    } catch (err: any) {
      setError(err.message || '처리 중 오류가 발생했습니다.');
    }
  };

  // V45.4: 계약만료 확정 기능 삭제됨

  // 상태 뱃지 렌더링 (V45.4: 계약연장 예약 추가)
  const renderStatusBadge = () => {
    if (!item) return null;

    const badges = [];

    // V45.4: 계약 연장 예약 뱃지
    if (item.contractExtensionPlanned && item.contractExtensionPlannedEffectiveDate) {
      badges.push(
        <span
          key="extension"
          style={{
            padding: '4px 10px',
            background: '#ecfdf5',
            color: '#059669',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
          }}
        >
          계약 연장 예정 ({item.contractExtensionPlannedEffectiveDate})
        </span>
      );
    }

    if (item.terminationPlanned && item.terminationPlannedEndDate) {
      badges.push(
        <span
          key="termination"
          style={{
            padding: '4px 10px',
            background: '#fef2f2',
            color: '#dc2626',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
          }}
        >
          퇴사 예정 ({item.terminationPlannedEndDate})
        </span>
      );
    }

    if (item.employmentTypeChangePlanned && item.employmentTypeChangePlannedEffectiveDate) {
      badges.push(
        <span
          key="typeChange"
          style={{
            padding: '4px 10px',
            background: '#eff6ff',
            color: '#2563eb',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
          }}
        >
          고용형태 변경 예정 ({item.employmentTypeChangePlannedEffectiveDate})
        </span>
      );
    }

    if (item.status === 'expiring' && !item.terminationPlanned && !item.contractExtensionPlanned) {
      badges.push(
        <span
          key="expiring"
          style={{
            padding: '4px 10px',
            background: '#fffbeb',
            color: '#d97706',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
          }}
        >
          계약 만료 임박
        </span>
      );
    }

    return badges.length > 0 ? <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>{badges}</div> : null;
  };

  if (!isOpen || !item) return null;

  const isLoading =
    scheduleTermination.isLoading ||
    changeTerminationDate.isLoading ||
    cancelTermination.isLoading ||
    scheduleExtension.isLoading ||
    cancelExtension.isLoading ||
    scheduleTypeChange.isLoading ||
    cancelTypeChange.isLoading;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      {...overlayProps}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '700px',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--color-gray-200)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {forceTab && onBack && (
              <button
                onClick={onBack}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: 'var(--color-gray-500)',
                  display: 'flex',
                  alignItems: 'center',
                }}
                title="다시 선택"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
              {forceTab
                ? { termination: '퇴사 예약', extension: '계약 연장', typeChange: '고용형태 변경', leaveOfAbsence: '휴직 관리' }[forceTab]
                : '고용 상세 관리'}
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 직원 정보 영역 */}
        <div
          style={{
            padding: '20px 24px',
            background: 'var(--color-gray-50)',
            borderBottom: '1px solid var(--color-gray-200)',
          }}
        >
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '6px' }}>{item.employeeName}</div>
            <div style={{ fontSize: '14px', color: 'var(--color-gray-600)' }}>
              {item.employeeNumber && <span>{item.employeeNumber} · </span>}
              {item.email && <span>{item.email}</span>}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '12px' }}>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginBottom: '4px' }}>고용형태</div>
              <div style={{ fontSize: '14px', fontWeight: 500 }}>{item.employmentTypeName}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginBottom: '4px' }}>계약 종료일</div>
              <div style={{ fontSize: '14px', fontWeight: 500 }}>{item.endDate || '-'}</div>
            </div>
          </div>

          {renderStatusBadge()}

          {isLocked && (
            <div
              style={{
                marginTop: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 12px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                color: '#dc2626',
                fontSize: '13px',
              }}
            >
              <Lock size={16} />
              이미 실행되어 수정할 수 없습니다.
            </div>
          )}
        </div>

        {/* V45.4: 탭 영역 — V253: forceTab 지정 시 숨김 */}
        {!forceTab && (
          <div
            style={{
              display: 'flex',
              borderBottom: '1px solid var(--color-gray-200)',
              background: 'white',
            }}
          >
            {[
              { id: 'termination' as TabType, label: '퇴사 예약', icon: <UserMinus size={16} /> },
              { id: 'extension' as TabType, label: '계약 연장', icon: <RefreshCw size={16} /> },
              { id: 'typeChange' as TabType, label: '고용형태 변경', icon: <ArrowRight size={16} /> },
              // V66 Phase 2: 휴직 탭
              { id: 'leaveOfAbsence' as TabType, label: '휴직', icon: <PauseCircle size={16} /> },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setError('');
                  setSuccessMessage('');
                }}
                style={{
                  flex: 1,
                  padding: '14px 16px',
                  background: activeTab === tab.id ? 'white' : 'transparent',
                  color: activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-gray-600)',
                  border: 'none',
                  borderBottom: activeTab === tab.id ? '2px solid var(--color-primary)' : '2px solid transparent',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                }}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* 탭 본문 */}
        <div style={{ padding: '24px' }}>
          {/* 성공/에러 메시지 */}
          {successMessage && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                color: '#166534',
                fontSize: '13px',
                marginBottom: '16px',
              }}
            >
              <CheckCircle size={16} />
              {successMessage}
            </div>
          )}

          {error && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                color: '#dc2626',
                fontSize: '13px',
                marginBottom: '16px',
              }}
            >
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* 퇴사 예약 탭 */}
          {activeTab === 'termination' && (
            <div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                  퇴사일 *
                </label>
                <input
                  type="date"
                  value={terminationDate}
                  onChange={(e) => {
                    setTerminationDate(e.target.value);
                    // V60: 실행일은 항상 적용일과 동기화
                    setTerminationPlannedRunDate(e.target.value);
                  }}
                  min={allowPastTerminationDate ? undefined : getToday()}
                  max={item.endDate || '9999-12-31'}
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
                {/* V46: 과거 퇴사일 입력 허용 옵션 */}
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '8px',
                    fontSize: '13px',
                    color: 'var(--color-gray-600)',
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allowPastTerminationDate}
                    onChange={(e) => {
                      setAllowPastTerminationDate(e.target.checked);
                      // V76: 옵션 끄면 과거 날짜를 정상 디폴트로 복구
                      if (!e.target.checked && terminationDate && terminationDate < getToday()) {
                        const defaultDate = item.endDate || getDefaultDate(30);
                        setTerminationDate(defaultDate);
                      }
                    }}
                    disabled={isLocked}
                    style={{ width: '16px', height: '16px', cursor: isLocked ? 'not-allowed' : 'pointer' }}
                  />
                  과거 퇴사일 입력 허용 (퇴사 처리 누락 시 사용)
                </label>
                {allowPastTerminationDate && (
                  <div
                    style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      background: '#fef3c7',
                      border: '1px solid #fcd34d',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#92400e',
                    }}
                  >
                    과거 날짜 선택 시 즉시 퇴사 처리됩니다. 신중히 입력해주세요.
                  </div>
                )}
              </div>

              {/* V254: 예약 실행일 */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                  예약 실행일
                </label>
                <input
                  type="date"
                  value={terminationPlannedRunDate}
                  readOnly
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: 'var(--color-gray-100)',
                  }}
                />
                <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--color-gray-500)' }}>
                  예약 실행일은 퇴사일과 동일하게 자동 설정됩니다.
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                  퇴사 사유 *
                </label>
                <select
                  value={terminationReasonCode}
                  onChange={(e) => setTerminationReasonCode(e.target.value as TerminationReasonCode)}
                  disabled={isLocked || item.terminationPlanned}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                >
                  {Object.entries(TERMINATION_REASON_NAMES).map(([code, name]) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                  코멘트 (선택, 최대 200자)
                </label>
                <textarea
                  value={terminationNote}
                  onChange={(e) => setTerminationNote(e.target.value)}
                  placeholder="퇴사 사유에 대한 추가 설명을 입력해주세요"
                  rows={3}
                  maxLength={200}
                  disabled={isLocked || item.terminationPlanned}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '4px', textAlign: 'right' }}>
                  {terminationNote.length} / 200
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleScheduleOrChangeTermination}
                  disabled={readOnly || isLocked || isLoading}
                  style={{
                    flex: 1,
                    padding: '10px 20px',
                    background: isLocked ? 'var(--color-gray-300)' : '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: isLocked || isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.7 : 1,
                  }}
                >
                  {isLoading ? '처리 중...' : item.terminationPlanned ? '날짜 변경' : '퇴사 예약'}
                </button>

                {item.terminationPlanned && (
                  <button
                    onClick={handleCancelTermination}
                    disabled={readOnly || isLocked || isLoading}
                    style={{
                      flex: 1,
                      padding: '10px 20px',
                      background: 'white',
                      color: '#ef4444',
                      border: '1px solid #ef4444',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: isLocked || isLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    예약 취소
                  </button>
                )}
              </div>

              <div
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  background: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: '#92400e',
                }}
              >
                퇴사 예약 시 퇴사일 00:15에 자동 실행됩니다.
              </div>
            </div>
          )}

          {/* V45.7: 계약 연장 예약 탭 */}
          {activeTab === 'extension' && (
            <div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                  새 계약 시작일 *
                </label>
                <input
                  type="date"
                  value={extensionEffectiveDate}
                  onChange={(e) => {
                    setExtensionEffectiveDate(e.target.value);
                    // V60: 실행일은 항상 적용일과 동기화
                    setExtensionPlannedRunDate(e.target.value);
                  }}
                  min={item.endDate ? getNextDay(item.endDate) : getToday()}
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* V254: 예약 실행일 */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                  예약 실행일
                </label>
                <input
                  type="date"
                  value={extensionPlannedRunDate}
                  readOnly
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: 'var(--color-gray-100)',
                  }}
                />
                <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--color-gray-500)' }}>
                  예약 실행일은 적용일과 동일하게 자동 설정됩니다.
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                  새 계약 종료일 *
                </label>
                <input
                  type="date"
                  value={extensionNewEndDate}
                  onChange={(e) => setExtensionNewEndDate(e.target.value)}
                  min={extensionEffectiveDate || (item.endDate ? getNextDay(item.endDate) : getToday())}
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                  사유 (선택)
                </label>
                <textarea
                  value={extensionReason}
                  onChange={(e) => setExtensionReason(e.target.value)}
                  placeholder="계약 연장 사유를 입력해주세요"
                  rows={2}
                  maxLength={200}
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {item.terminationPlanned && (
                <div
                  style={{
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px',
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#92400e',
                  }}
                >
                  <AlertCircle size={16} />
                  현재 퇴사 예약({item.terminationPlannedEndDate})이 있습니다. 계약 연장 시 자동 취소됩니다.
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px' }}>
                {/* V271: 예약 상태에서는 예약변경 제거, 신규 등록만 표시 */}
                {!item.contractExtensionPlanned && (
                  <button
                    onClick={handleScheduleOrExtendContract}
                    disabled={readOnly || isLocked || isLoading}
                    style={{
                      flex: 1,
                      padding: '10px 20px',
                      background: isLocked ? 'var(--color-gray-300)' : 'var(--color-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: isLocked || isLoading ? 'not-allowed' : 'pointer',
                      opacity: isLoading ? 0.7 : 1,
                    }}
                  >
                    {isLoading ? '처리 중...' : '연장 예약'}
                  </button>
                )}

                {item.contractExtensionPlanned && (
                  <>
                    <button
                      onClick={handleCancelExtension}
                      disabled={readOnly || isLocked || isLoading}
                      style={{
                        flex: 1,
                        padding: '10px 20px',
                        background: 'white',
                        color: 'var(--color-primary)',
                        border: '1px solid var(--color-primary)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: isLocked || isLoading ? 'not-allowed' : 'pointer',
                      }}
                    >
                      예약 취소
                    </button>
                  </>
                )}
              </div>
              {/* V271: 예약 변경 안내 */}
              {item.contractExtensionPlanned && (
                <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#6b7280' }}>
                  예약 내용을 변경하려면 기존 예약을 취소 후 다시 등록해주세요.
                </p>
              )}

              <div
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  background: '#ecfdf5',
                  border: '1px solid #a7f3d0',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: '#065f46',
                }}
              >
                계약 연장 예약 시 연장 적용일 00:05에 적용됩니다. 과거일은 즉시 적용됩니다.
              </div>
            </div>
          )}

          {/* 고용형태 변경 탭 */}
          {activeTab === 'typeChange' && (
            <div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                  새 고용형태 *
                </label>
                <select
                  value={newEmploymentType}
                  onChange={(e) => setNewEmploymentType(e.target.value as EmploymentTypeCode)}
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                >
                  {EMPLOYMENT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.code} value={opt.code}>
                      {opt.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                  새 고용형태 시작일 *
                </label>
                <input
                  type="date"
                  value={typeChangeEffectiveDate}
                  onChange={(e) => {
                    setTypeChangeEffectiveDate(e.target.value);
                    // V60: 실행일은 항상 적용일과 동기화
                    setTypeChangePlannedRunDate(e.target.value);
                  }}
                  min={item.endDate ? getNextDay(item.endDate) : getToday()}
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* V254: 예약 실행일 */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                  예약 실행일
                </label>
                <input
                  type="date"
                  value={typeChangePlannedRunDate}
                  readOnly
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: 'var(--color-gray-100)',
                  }}
                />
                <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--color-gray-500)' }}>
                  예약 실행일은 적용일과 동일하게 자동 설정됩니다.
                </div>
              </div>

              {/* V45.4: 파견/인턴/계약직 선택 시 만료일 입력 필드 */}
              {isEndDateRequired(newEmploymentType) && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                    계약 만료일 *
                  </label>
                  <input
                    type="date"
                    value={typeChangeEndDate}
                    onChange={(e) => setTypeChangeEndDate(e.target.value)}
                    min={typeChangeEffectiveDate || getToday()}
                    disabled={isLocked}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--color-gray-300)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                    {EMPLOYMENT_TYPE_OPTIONS.find(o => o.code === newEmploymentType)?.name}는 계약 만료일 입력이 필수입니다.
                  </div>
                </div>
              )}

              {/* V20-FIX: 정규직 전환 시 이전경력 입력 (priorCareerYears 누적) */}
              {newEmploymentType === 'REGULAR' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                    이전경력 (선택)
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="number"
                      value={conversionBonusYears}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        // 소수점이 포함되면 에러 표시
                        if (inputValue.includes('.') || inputValue.includes(',')) {
                          setConversionBonusYearsError('정수만 입력 가능합니다');
                        } else {
                          setConversionBonusYearsError('');
                        }
                        const val = parseInt(inputValue, 10);
                        setConversionBonusYears(isNaN(val) ? 0 : Math.max(0, val));
                      }}
                      min={0}
                      step={1}
                      disabled={isLocked}
                      style={{
                        width: '100px',
                        padding: '10px 12px',
                        border: `1px solid ${conversionBonusYearsError ? '#dc2626' : 'var(--color-gray-300)'}`,
                        borderRadius: '8px',
                        fontSize: '14px',
                        textAlign: 'right',
                      }}
                    />
                    <span style={{ fontSize: '14px', color: 'var(--color-gray-600)' }}>년</span>
                  </div>
                  {conversionBonusYearsError ? (
                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#dc2626' }}>
                      {conversionBonusYearsError}
                    </div>
                  ) : (
                    <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--color-gray-500)' }}>
                      정규직 전환 시 인정받는 이전경력 연한 (기존 이전경력에 누적됨, 정수만)
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>
                  사유 (선택, 최대 200자)
                </label>
                <textarea
                  value={typeChangeReason}
                  onChange={(e) => setTypeChangeReason(e.target.value)}
                  placeholder="고용형태 변경 사유를 입력해주세요"
                  rows={3}
                  maxLength={200}
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '4px', textAlign: 'right' }}>
                  {typeChangeReason.length} / 200
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                {/* V271: 예약 상태에서는 예약변경 제거, 신규 등록만 표시 */}
                {!item.employmentTypeChangePlanned && (
                  <button
                    onClick={handleScheduleTypeChange}
                    disabled={readOnly || isLocked || isLoading}
                    style={{
                      flex: 1,
                      padding: '10px 20px',
                      background: isLocked ? 'var(--color-gray-300)' : 'var(--color-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: isLocked || isLoading ? 'not-allowed' : 'pointer',
                      opacity: isLoading ? 0.7 : 1,
                    }}
                  >
                    {isLoading ? '처리 중...' : '변경 예약'}
                  </button>
                )}

                {/* V45.2→V271: 고용형태 변경 예약 취소 버튼만 유지 */}
                {item.employmentTypeChangePlanned && (
                  <button
                    onClick={handleCancelTypeChange}
                    disabled={readOnly || isLocked || isLoading}
                    style={{
                      flex: 1,
                      padding: '10px 20px',
                      background: 'white',
                      color: '#2563eb',
                      border: '1px solid #2563eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: isLocked || isLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    예약 취소
                  </button>
                )}
              </div>
              {/* V271: 예약 변경 안내 */}
              {item.employmentTypeChangePlanned && (
                <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#6b7280' }}>
                  예약 내용을 변경하려면 기존 예약을 취소 후 다시 등록해주세요.
                </p>
              )}

              <div
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: '#1e40af',
                }}
              >
                고용형태 변경은 적용일 00:10에 적용됩니다.
              </div>
            </div>
          )}

          {/* V45.4: 계약만료 확정 탭 삭제됨 */}

          {/* V66 Phase 2: 휴직 관리 탭 */}
          {activeTab === 'leaveOfAbsence' && item && (
            <LeaveOfAbsenceSection
              employeeId={item.employeeId}
              employeeName={item.employeeName}
              readOnly={readOnly}
              onChanged={onSuccess}
            />
          )}
        </div>

        {/* 푸터 */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--color-gray-200)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: 'white',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
